
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Play, Pause, RefreshCw, AlertTriangle, CheckCircle, Clock } from "lucide-react";

interface TaskFlow {
  id: string;
  name: string;
  status: 'running' | 'paused' | 'completed' | 'error';
  progress: number;
  totalSteps: number;
  completedSteps: number;
  nextExecution?: Date;
  lastRun?: Date;
  description: string;
}

interface TaskExecution {
  id: string;
  taskFlowId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startTime: Date;
  endTime?: Date;
  duration?: number;
  details: string;
}

export const TaskOrchestrator: React.FC = () => {
  const [taskFlows] = useState<TaskFlow[]>([
    {
      id: '1',
      name: 'Daily Candidate Processing',
      status: 'running',
      progress: 75,
      totalSteps: 8,
      completedSteps: 6,
      nextExecution: new Date(Date.now() + 2 * 60 * 60 * 1000),
      lastRun: new Date(Date.now() - 30 * 60 * 1000),
      description: 'Process new candidates, send welcome emails, and create initial tasks'
    },
    {
      id: '2',
      name: 'Weekly Client Reports',
      status: 'paused',
      progress: 0,
      totalSteps: 5,
      completedSteps: 0,
      nextExecution: new Date(Date.now() + 24 * 60 * 60 * 1000),
      description: 'Generate and send weekly progress reports to all active clients'
    },
    {
      id: '3',
      name: 'Interview Reminder System',
      status: 'completed',
      progress: 100,
      totalSteps: 3,
      completedSteps: 3,
      lastRun: new Date(Date.now() - 60 * 60 * 1000),
      description: 'Send reminder emails 24h and 2h before scheduled interviews'
    }
  ]);

  const [executions] = useState<TaskExecution[]>([
    {
      id: '1',
      taskFlowId: '1',
      status: 'completed',
      startTime: new Date(Date.now() - 60 * 60 * 1000),
      endTime: new Date(Date.now() - 30 * 60 * 1000),
      duration: 30,
      details: 'Processed 15 new candidates, sent 12 welcome emails'
    },
    {
      id: '2',
      taskFlowId: '3',
      status: 'completed',
      startTime: new Date(Date.now() - 2 * 60 * 60 * 1000),
      endTime: new Date(Date.now() - 90 * 60 * 1000),
      duration: 30,
      details: 'Sent 8 interview reminders, 2 confirmations received'
    },
    {
      id: '3',
      taskFlowId: '2',
      status: 'failed',
      startTime: new Date(Date.now() - 3 * 60 * 60 * 1000),
      duration: 5,
      details: 'Failed to generate report for client XYZ - missing data'
    }
  ]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />;
      case 'paused':
        return <Pause className="h-4 w-4 text-yellow-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'paused':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'error':
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="flows" className="space-y-4">
        <TabsList>
          <TabsTrigger value="flows">Task Flows</TabsTrigger>
          <TabsTrigger value="executions">Execution History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="flows" className="space-y-4">
          <div className="grid gap-4">
            {taskFlows.map((flow) => (
              <Card key={flow.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(flow.status)}
                      <CardTitle className="text-lg">{flow.name}</CardTitle>
                      <Badge variant="outline" className={getStatusColor(flow.status)}>
                        {flow.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <Play className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <Pause className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{flow.description}</p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{flow.completedSteps}/{flow.totalSteps} steps</span>
                    </div>
                    <Progress value={flow.progress} className="h-2" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {flow.nextExecution && (
                      <div>
                        <span className="text-muted-foreground">Next run:</span>
                        <p className="font-medium">{flow.nextExecution.toLocaleString()}</p>
                      </div>
                    )}
                    {flow.lastRun && (
                      <div>
                        <span className="text-muted-foreground">Last run:</span>
                        <p className="font-medium">{flow.lastRun.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="executions" className="space-y-4">
          <div className="grid gap-4">
            {executions.map((execution) => {
              const flow = taskFlows.find(f => f.id === execution.taskFlowId);
              return (
                <Card key={execution.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(execution.status)}
                        <span className="font-medium">{flow?.name}</span>
                        <Badge variant="outline" className={getStatusColor(execution.status)}>
                          {execution.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {execution.duration && `${execution.duration}min`}
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">{execution.details}</p>
                    
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Started: {execution.startTime.toLocaleString()}</span>
                      {execution.endTime && (
                        <span>Ended: {execution.endTime.toLocaleString()}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
